// Holds the user's current selection AND mirrors it server-side via /holds.
// Optimistic: flip the seat in the local set, then call the API; on failure,
// revert and surface a message.

import { ApiRequestError, api } from '@/lib/api.ts';
import { useCallback, useRef, useState } from 'react';

type HoldsResponse = { expiresAt: string; seatIds: string[] };

export type HoldState = {
  selected: Set<string>;
  expiresAt: string | null;
  error: { message: string; conflictSeatIds?: string[] } | null;
  pending: boolean;
};

export function useHoldManager(eventId: string, sessionToken: string) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [error, setError] = useState<HoldState['error']>(null);
  const [pending, setPending] = useState(false);

  // Latest selection ref (avoid stale closures in unload handler).
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const toggle = useCallback(
    async (seatId: string) => {
      const isSelected = selectedRef.current.has(seatId);
      const next = new Set(selectedRef.current);
      if (isSelected) next.delete(seatId);
      else next.add(seatId);
      setSelected(next);
      setPending(true);
      setError(null);

      try {
        if (next.size === 0) {
          await api('/holds', {
            method: 'DELETE',
            body: { sessionToken },
          });
          setExpiresAt(null);
        } else if (isSelected) {
          await api('/holds', {
            method: 'DELETE',
            body: { sessionToken, seatIds: [seatId] },
          });
          // Keep the existing countdown — removing a seat must NOT restart the
          // hold window; the remaining seats keep their original expiry.
        } else {
          const res = await api<HoldsResponse>('/holds', {
            method: 'POST',
            body: { eventId, seatIds: [seatId], sessionToken },
          });
          setExpiresAt(res.expiresAt);
        }
      } catch (err) {
        // Revert
        setSelected(selectedRef.current);
        if (err instanceof ApiRequestError) {
          setError({
            message: err.body.error.message,
            conflictSeatIds: err.body.error.conflictSeatIds,
          });
          // For conflicts, drop the offending seat from selection so the UI matches reality.
          const conflicts = err.body.error.conflictSeatIds;
          if (conflicts && conflicts.length > 0) {
            setSelected((prev) => {
              const n = new Set(prev);
              for (const id of conflicts) n.delete(id);
              return n;
            });
          }
        } else {
          setError({ message: 'Hold işlemi başarısız' });
        }
      } finally {
        setPending(false);
      }
    },
    [eventId, sessionToken],
  );

  const clearAll = useCallback(async () => {
    if (selectedRef.current.size === 0) return;
    setSelected(new Set());
    setExpiresAt(null);
    try {
      await api('/holds', { method: 'DELETE', body: { sessionToken } });
    } catch {
      // best-effort
    }
  }, [sessionToken]);

  const restoreFromServer = useCallback((seatIds: string[], holdExpiresAt?: string | null) => {
    setSelected(new Set(seatIds));
    if (holdExpiresAt !== undefined) setExpiresAt(holdExpiresAt);
  }, []);

  // sendBeacon would be ideal but only supports POST; we'd need a /holds/release
  // POST endpoint. Skipping for now — server-side cron cleans expired holds
  // every minute and lazy filtering hides them from /state instantly.

  return { selected, expiresAt, error, pending, toggle, clearAll, restoreFromServer };
}
